import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Textarea } from '../../../components/ui/textarea';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  abnahmeApi,
  MANUELL_STATUS_LABEL,
  type LaufDto,
  type LaufErgebnisDto,
  type ManuellStatus,
} from '../../../lib/api/abnahme';

type LaufMitErgebnissen = LaufDto & { ergebnisse: LaufErgebnisDto[] };

const KNOEPFE: Array<{ status: ManuellStatus; klasse: string }> = [
  { status: 'bestanden', klasse: 'border-emerald-600 bg-emerald-600 text-white' },
  { status: 'fehlgeschlagen', klasse: 'border-red-600 bg-red-600 text-white' },
  { status: 'blockiert', klasse: 'border-amber-600 bg-amber-600 text-white' },
  { status: 'uebersprungen', klasse: 'border-slate-500 bg-slate-500 text-white' },
];

/**
 * Ein manueller Testlauf zum Abhaken (docs/abnahme-plan.md, Phase 4).
 *
 * Geprüft wird auf dem Testsystem, erfasst hier. „Fehlgeschlagen" legt auf Wunsch
 * gleich ein Ticket an, das Story, Schritte und Erwartung schon kennt.
 */
export default function ManuellerLaufPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const darfErfassen = usePermission(P.abnahme.testlaeufe.testlaeufe_durchfuehren);
  const [lauf, setLauf] = useState<LaufMitErgebnissen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [notizen, setNotizen] = useState<Record<number, string>>({});
  const [mitTicket, setMitTicket] = useState<Record<number, boolean>>({});
  const [arbeitet, setArbeitet] = useState<number | null>(null);

  const laden = useCallback(async () => {
    try {
      setLauf(await abnahmeApi.lauf(Number(id)));
      setFehler(null);
    } catch (grund) {
      setFehler(grund instanceof Error ? grund.message : 'Der Lauf ließ sich nicht laden.');
    }
  }, [id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const gruppen = useMemo(() => {
    const karte = new Map<string, LaufErgebnisDto[]>();
    for (const ergebnis of lauf?.ergebnisse ?? []) {
      const story = (ergebnis.storyKey ?? '—').split('.')[0];
      karte.set(story, [...(karte.get(story) ?? []), ergebnis]);
    }
    return [...karte.entries()];
  }, [lauf]);

  const offen = (lauf?.ergebnisse ?? []).filter((e) => e.status === 'offen').length;
  const abgeschlossen = !!lauf?.abgeschlossenAt;
  const erfassbar = darfErfassen && !abgeschlossen;

  const erfassen = async (ergebnis: LaufErgebnisDto, status: ManuellStatus) => {
    if (!ergebnis.id) return;
    setArbeitet(ergebnis.id);
    try {
      const antwort = await abnahmeApi.ergebnisErfassen(ergebnis.id, {
        status,
        meldung: notizen[ergebnis.id] ?? ergebnis.meldung ?? undefined,
        ticketAnlegen: status === 'fehlgeschlagen' && (mitTicket[ergebnis.id] ?? true),
      });
      if (antwort?.ticketRef && !ergebnis.ticketRef) toast.success(`Ticket ${antwort.ticketRef} angelegt.`);
      await laden();
    } catch (grund) {
      toast.error(grund instanceof Error ? grund.message : 'Das Ergebnis wurde nicht übernommen.');
    } finally {
      setArbeitet(null);
    }
  };

  const abschliessen = async () => {
    if (!lauf) return;
    try {
      await abnahmeApi.laufAbschliessen(lauf.id);
      toast.success('Lauf abgeschlossen.');
      await laden();
    } catch (grund) {
      toast.error(grund instanceof Error ? grund.message : 'Der Lauf ließ sich nicht abschließen.');
    }
  };

  if (fehler) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/abnahme/testlaeufe')}>
          <ArrowLeft className="h-4 w-4" /> Zu den Testläufen
        </Button>
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">{fehler}</div>
      </div>
    );
  }
  if (!lauf) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Lauf wird geladen …
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="space-y-2">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/abnahme/testlaeufe')}>
          <ArrowLeft className="h-4 w-4" /> Zu den Testläufen
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold leading-8">{lauf.titel ?? 'Manueller Lauf'}</h1>
          {abgeschlossen ? <Badge variant="secondary">abgeschlossen</Badge> : <Badge>läuft</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          Version {lauf.version ?? '—'} · angelegt von {lauf.gestartetVon ?? 'unbekannt'} ·{' '}
          {lauf.bestanden} bestanden, {lauf.fehlgeschlagen} fehlgeschlagen oder blockiert,{' '}
          {lauf.uebersprungen} übersprungen, {offen} offen
        </p>
        {erfassbar && (
          <Button variant="outline" size="sm" onClick={() => void abschliessen()}>
            Lauf abschließen{offen > 0 ? ` (${offen} noch offen)` : ''}
          </Button>
        )}
      </div>

      {gruppen.map(([story, ergebnisse]) => (
        <Card key={story}>
          <CardHeader>
            <CardTitle className="font-mono text-sm">{story}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {ergebnisse.map((ergebnis) => (
              <div key={ergebnis.id} className="space-y-2 border-b pb-4 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{ergebnis.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {ergebnis.storyKey}
                      {ergebnis.testfall?.herkunft ? ` · ${ergebnis.testfall.herkunft}` : ''}
                    </p>
                  </div>
                  <Badge variant={ergebnis.status === 'offen' ? 'outline' : 'secondary'}>
                    {MANUELL_STATUS_LABEL[ergebnis.status as ManuellStatus] ?? ergebnis.status}
                  </Badge>
                </div>
                {ergebnis.testfall?.schritte && (
                  <CardDescription className="whitespace-pre-wrap">{ergebnis.testfall.schritte}</CardDescription>
                )}
                {ergebnis.testfall?.erwartung && (
                  <p className="whitespace-pre-wrap text-sm">
                    <span className="font-medium">Erwartet: </span>
                    {ergebnis.testfall.erwartung}
                  </p>
                )}
                {erfassbar ? (
                  <>
                    <Textarea
                      rows={2}
                      placeholder="Beobachtung (bei Fehlschlag Pflicht fürs Ticket)"
                      value={notizen[ergebnis.id ?? 0] ?? ergebnis.meldung ?? ''}
                      onChange={(event) =>
                        setNotizen((vorher) => ({ ...vorher, [ergebnis.id ?? 0]: event.target.value }))
                      }
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {KNOEPFE.map(({ status, klasse }) => (
                        <button
                          key={status}
                          type="button"
                          disabled={arbeitet === ergebnis.id}
                          onClick={() => void erfassen(ergebnis, status)}
                          className={`rounded-md border px-3 py-1.5 text-sm ${ergebnis.status === status ? klasse : ''}`}
                        >
                          {MANUELL_STATUS_LABEL[status]}
                        </button>
                      ))}
                      {!ergebnis.ticketRef && (
                        <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={mitTicket[ergebnis.id ?? 0] ?? true}
                            onChange={(event) =>
                              setMitTicket((vorher) => ({ ...vorher, [ergebnis.id ?? 0]: event.target.checked }))
                            }
                          />
                          bei Fehlschlag Ticket anlegen
                        </label>
                      )}
                    </div>
                  </>
                ) : (
                  ergebnis.meldung && <p className="whitespace-pre-wrap text-sm">{ergebnis.meldung}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {ergebnis.ticketRef ? `Ticket ${ergebnis.ticketRef}` : ''}
                  {ergebnis.ticketRef && ergebnis.erfasstVon ? ' · ' : ''}
                  {ergebnis.erfasstVon ? `erfasst von ${ergebnis.erfasstVon}` : ''}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
